<template>
    <div>
        <b-row>
            <b-col class="d-flex justify-content-between align-items-center">
                <slot name="header">
                    <text-header>Спецификация</text-header>
                </slot>
            </b-col>
        </b-row>
        <b-table id="my-table" :fields="fields" :items="items" class="table-atmo" :class="{ 'is-table-invalid': v.$invalid || specErrors }" details-td-class="py-1" show-empty small responsive>
            <template #cell(index)="data">
                {{ data.index + 1 }}
            </template>
            <template #row-details="data">
                <template v-if="getPurchaseDeliverable(data.item.id).ktru_item">
                    <p><b>Рег.номер ТРУ:</b> {{ getPurchaseDeliverable(data.item.id).ktru_item.reg_number }}</p>
                    <p><b>Группа ТРУ:</b> {{ getPurchaseDeliverable(data.item.id).ktru_item.ktru_group_name }}</p>
                    <p><b>Наименование:</b> {{ getPurchaseDeliverable(data.item.id).ktru_item.name }}</p>
                    <p><b>Тип:</b> {{ getPurchaseDeliverable(data.item.id).ktru_item.type }}</p>
                    <p v-if="getPurchaseDeliverable(data.item.id).ktru_item.okpd_code"><b>Код ОКПД2:</b> {{ getPurchaseDeliverable(data.item.id).ktru_item.okpd_code }}</p>
                    <p v-if="getPurchaseDeliverable(data.item.id).ktru_item.country_of_origin_name"><b>Страна происхождения:</b> {{ getPurchaseDeliverable(data.item.id).ktru_item.country_of_origin_name }}</p>
                    <p v-if="getPurchaseDeliverable(data.item.id).ktru_item.description"><b>Описание:</b> {{ getPurchaseDeliverable(data.item.id).ktru_item.description }}</p>
                    <p v-if="getPurchaseDeliverable(data.item.id).ktru_item.specs && getPurchaseDeliverable(data.item.id).ktru_item.specs.length > 0"><b>Характеристики:</b></p>
                    <ul v-if="getPurchaseDeliverable(data.item.id).ktru_item.specs && getPurchaseDeliverable(data.item.id).ktru_item.specs.length > 0">
                        <li v-for="(spec, key) in getPurchaseDeliverable(data.item.id).ktru_item.specs" :key="'spec' + key">
                            <span class="fw-600">{{ spec.name }}</span
                            >: {{ spec.value }}
                        </li>
                    </ul>
                </template>
                <template v-else>&mdash;</template>
            </template>
            <template #cell(specs)="data">
                <action-button-small :title="(data.detailsShowing ? 'Скрыть' : 'Показать') + ' характеристики'" :icon="data.detailsShowing ? 'eye-slash' : 'eye'" @click="data.toggleDetails" />
                {{ getPurchaseDeliverable(data.item.id).ktru_item.name }}
            </template>
            <template #cell(okei)="data">
                <template v-if="getPurchaseDeliverable(data.item.id).ktru_item.okei_local_symbol">{{ getPurchaseDeliverable(data.item.id).ktru_item.okei_local_symbol }}</template>
            </template>
            <template #cell(price)="data">
                <currency-input v-model="data.item.price" :class="v.$each[data.index]?.price && !v.$each[data.index].price.$invalid ? 'is-valid' : 'is-invalid'" class="form-control fs-14 text-right currency-input mb-0" currency="RUB" locale="ru" @focus="$event.target?.select()" />
            </template>
            <template #cell(vat)="data">
                <template v-if="withoutVat">
                    <input class="form-control fs-14 is-valid" disabled type="text" value="Без НДС" />
                </template>
                <template v-else>
                    <v-select
                        v-model="data.item.vat"
                        :calculate-position="withPopper"
                        :class="{ 'is-invalid': data.item.vat === null, 'd-inline-block': true, 'w-75': true }"
                        :clearable="false"
                        :options="vatDictionary"
                        :reduce="(item) => item.id"
                        :searchable="false"
                        append-to-body
                        label="title"
                        placeholder="НДС">
                        <!-- eslint-disable-next-line vue/no-unused-vars  -->
                        <template #no-options="{ search, searching, loading }"> Записей, соответствующих вашему запросу, не найдено.</template>
                    </v-select>
                    <span v-b-popover.hover.top="'Применить ко всем позициям спецификации'" class="d-inline-block pl-1">
                        <action-button-small :disabled="data.item.vat === null" icon="chevron-double-down" @click="applyAll(data.item.vat)" />
                    </span>
                </template>
            </template>
            <template #bottom-row>
                <b-td v-if="v.$invalid || specErrors" colspan="5" class="align-text-top py-1 pl-1">
                    <b-form-invalid-feedback :state="!v.$invalid && !specErrors" class="fs-14">{{ specErrorMessage }}</b-form-invalid-feedback>
                </b-td>
            </template>
            <template #empty>
                <div class="w-100 text-center">Не добавлено позиций спецификации</div>
            </template>
        </b-table>
    </div>
</template>

<script>
import { CurrencyInput } from 'vue-currency-input';
import { withPopper } from '@/helpers';

export default {
    name: 'fl44-unknown-volume-form-row-proposal-specification-table',
    components: {
        CurrencyInput
    },
    props: {
        value: {
            type: Array,
            required: true
        },
        purchaseDeliverables: {
            type: Array,
            required: true
        },
        v: {
            type: Object,
            required: true
        },
        vatDictionary: {
            type: Array,
            required: true
        },
        withoutVat: {
            type: Boolean,
            default: false
        },
        specErrors: {
            type: Boolean
        },
        specErrorMessage: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            fields: [
                { key: 'index', label: '№', tdClass: 'align-text-top text-nowrap py-1', thClass: 'py-1', thStyle: { width: '1px' } },
                { key: 'specs', label: 'Наименование товара (работ, услуг)', tdClass: 'align-text-top py-1', thClass: 'text-nowrap py-1', thStyle: { minWidth: '400px' } },
                { key: 'okei', label: 'Ед.изм.', tdClass: 'align-text-top py-1 text-nowrap text-right', thClass: 'py-1 text-right', thStyle: { width: '100px' } },
                { key: 'price', label: 'Цена за ед. с НДС, руб.', tdClass: 'align-text-top py-1 text-nowrap text-right', thClass: 'py-1 text-right', thStyle: { width: '165px' } },
                { key: 'vat', label: 'НДС, %', tdClass: 'align-text-top py-1 text-nowrap', thClass: 'py-1', thStyle: { width: '175px' } },
            ],
        };
    },
    methods: {
        withPopper,
        applyAll(vat) {
            this.items.forEach((val) => (val.vat = vat));
        },
        getPurchaseDeliverable(id) {
            return this.purchaseDeliverables.find((x) => x.id === id);
        }
    },
    computed: {
        items: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        }
    }
};
</script>
<style scoped>
.amount-input {
    height: 30px;
}
.is-table-invalid {
    border: 1px solid red;
    border-radius: 4px;
}
.form-control.is-invalid,
.form-control.is-valid {
    background-image: none;
    padding-right: 0.3rem !important;
}
</style>
